/**
 * Правка собственного профиля: имя и аватар.
 *
 * Почта и пароль сюда не входят: это не «настройки внешнего вида», а действия,
 * после которых меняется вход в аккаунт. Каждое открывается своим окном по
 * отдельной кнопке, чтобы случайное нажатие «Сохранить» их не задело.
 *
 * Загруженную картинку сначала кадрируют: люди грузят панорамы и вертикальные
 * портреты, а аватар круглый — обрезать такое автоматически по центру значит
 * регулярно отрезать голову.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateProfile, useUploadAvatar } from "../api/queries";
import { cropSquare } from "../lib/image";
import { errorMessage } from "../lib/authForm";
import { useAuthStore } from "../stores/authStore";
import { Avatar } from "./Avatar";
import { AvatarCropper } from "./AvatarCropper";
import { ChangeEmailDialog } from "./ChangeEmailDialog";
import { Dialog } from "./Dialog";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { DeleteAccountDialog } from "./DeleteAccountDialog";

export function ProfileEditor({ className = "", onDone }: { className?: string; onDone: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const restore = useAuthStore((state) => state.restore);

  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();

  const [username, setUsername] = useState(user?.username ?? "");
  const [picked, setPicked] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [avatarKey, setAvatarKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [changingEmail, setChangingEmail] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removingAvatar, setRemovingAvatar] = useState(false);

  const applyCrop = async (crop: { x: number; y: number; size: number }) => {
    if (!picked) return;

    const file = picked;
    setPicked(null);
    setError(null);

    try {
      const square = await cropSquare(file, crop);
      setPreview(URL.createObjectURL(square));
      setAvatarKey(await uploadAvatar.mutateAsync(square));
    } catch (failure) {
      setPreview(null);
      setError(errorMessage(failure));
    }
  };

  /**
   * Удаление аватара применяется сразу, а не по «Сохранить»: человек нажал
   * «Удалить» и подтвердил — ждать от него ещё одного действия незачем, да и
   * картинка на экране обязана исчезнуть в тот же момент.
   */
  const removeAvatar = async () => {
    setError(null);
    setSaved(false);
    setRemovingAvatar(false);

    try {
      await updateProfile.mutateAsync({ avatarObjectKey: "", updateAvatar: true });
      setPreview(null);
      setAvatarKey(null);
      await restore();
      setRemoved(true);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  };

  const save = async () => {
    setError(null);
    setSaved(false);
    setRemoved(false);

    try {
      await updateProfile.mutateAsync({
        username: username.trim(),
        ...(avatarKey === null ? {} : { avatarObjectKey: avatarKey, updateAvatar: true }),
      });
      // Имя и аватар видны в шапке, поэтому сессию перечитываем: иначе там
      // останется прежнее до следующей перезагрузки.
      await restore();
      setSaved(true);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  };

  return (
    <div className={`space-y-6 border-t border-ink-800 pt-6 ${className}`}>
      <div className="flex flex-wrap items-center gap-4">
        <Avatar
          url={preview ?? user?.avatarUrl}
          username={user?.username ?? ""}
          role={user?.role}
          className="h-16 w-16 text-xl"
        />

        <div className="flex flex-wrap gap-2">
          <label className="btn-secondary cursor-pointer">
            {uploadAvatar.isPending ? t("profile.uploading") : t("profile.pickAvatar")}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) setPicked(file);
                event.target.value = "";
              }}
            />
          </label>

          {(preview || user?.avatarUrl) && (
            <button
              type="button"
              className="btn-ghost"
              disabled={updateProfile.isPending}
              onClick={() => setRemovingAvatar(true)}
            >
              {t("profile.removeAvatar")}
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="label" htmlFor="profile-username">
          {t("auth.field.username")}
        </label>
        <input
          id="profile-username"
          className="field"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
        />
      </div>

      {error && <p className="text-xs text-rust">{error}</p>}
      {saved && <p className="text-xs text-sage">{t("profile.saved")}</p>}
      {removed && <p className="text-xs text-sage">{t("profile.avatarRemoved")}</p>}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={updateProfile.isPending || uploadAvatar.isPending}
          onClick={() => void save()}
        >
          {updateProfile.isPending ? t("profile.saving") : t("common.save")}
        </button>
        <button type="button" className="btn-ghost" onClick={onDone}>
          {t("common.close")}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-ink-800 pt-6">
        <button type="button" className="btn-secondary" onClick={() => setChangingEmail(true)}>
          {t("profile.emailButton")}
        </button>
        <button type="button" className="btn-secondary" onClick={() => setChangingPassword(true)}>
          {t("profile.changePassword")}
        </button>
      </div>

      {/* Отдельно и последней: разрушительное действие не должно стоять в
          одном ряду с безобидными, куда палец попадает по привычке.
          Администратору кнопки нет: роли раздаёт только он, и без него
          панель осталась бы без хозяина — сервер такую просьбу отклонит. */}
      {user?.role !== "ROLE_ADMIN" && (
        <div className="border-t border-ink-800 pt-6">
          <button
            type="button"
            className="btn border border-rust/50 text-rust hover:bg-rust/10"
            onClick={() => setDeleting(true)}
          >
            {t("deletion.button")}
          </button>
        </div>
      )}

      {picked && (
        <AvatarCropper
          file={picked}
          onCancel={() => setPicked(null)}
          onApply={(crop) => void applyCrop(crop)}
        />
      )}
      {removingAvatar && (
        <Dialog title={t("profile.removeAvatarTitle")} onClose={() => setRemovingAvatar(false)}>
          <p className="text-sm text-ink-300">{t("profile.removeAvatarConfirm")}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={updateProfile.isPending}
              onClick={() => void removeAvatar()}
            >
              {t("profile.removeAvatarYes")}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setRemovingAvatar(false)}>
              {t("profile.removeAvatarNo")}
            </button>
          </div>
        </Dialog>
      )}
      {changingEmail && <ChangeEmailDialog onClose={() => setChangingEmail(false)} />}
      {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}
      {deleting && <DeleteAccountDialog onClose={() => setDeleting(false)} />}
    </div>
  );
}
